/**
 * characterCreationDraft — pure helpers for the B-007 CharacterCreationScene.
 *
 * Phaser-free by design. Holds:
 *   - The canonical 12-Vocation display order (re-exported from @game/shared
 *     so callers don't grow a transitive Phaser import path),
 *   - Realm picker option list (Verdana default-first per character-creation
 *     spec §5.2),
 *   - Name normalization (clamp + character-class strip + trim per spec §2),
 *   - Draft (de)serialization for the localStorage handoff to the eventual
 *     server-side persistence wire-up. The server side is OUT of scope this
 *     run (`OverworldRoom.ts` is being edited by B-013 in flight); a
 *     follow-up backlog entry consumes this draft on the next `OverworldRoom`
 *     join and persists to the `characters` SQLite table.
 *
 * See `docs/design/character-creation-scene.md` and GDD §6.5 / §16.14 / §17.11
 * for the source of truth.
 */
import {
  REALM_METADATA,
  RealmId,
  VOCATION_DISPLAY_ORDER,
  VOCATIONS,
  VocationKey,
  isRealmId,
} from '@game/shared';

/** localStorage key used for the client-state draft handoff. */
export const CHARACTER_DRAFT_STORAGE_KEY = 'rpg-gibli/character/draft';

/** Hard cap on the name length per spec §2 (sourced from the doc as 20). */
export const NAME_MAX_LENGTH = 20;

/**
 * The canonical 4×3 grid order, re-exported so the scene file does not need
 * to import @game/shared directly. Tests pin this against the shared list to
 * detect upstream churn early.
 */
export const VOCATION_GRID_ORDER: readonly VocationKey[] = VOCATION_DISPLAY_ORDER;

/** Display row for a single Vocation tile. */
export interface VocationDisplayRow {
  key: VocationKey;
  displayName: string;
  shortDescription: string;
}

/** Build the 12-row table consumed by the Vocation grid renderer. */
export function buildVocationDisplayRows(): VocationDisplayRow[] {
  return VOCATION_GRID_ORDER.map((key) => ({
    key,
    displayName: VOCATIONS[key].displayName,
    shortDescription: VOCATIONS[key].shortDescription,
  }));
}

/** Display row for a single Realm radio entry. */
export interface RealmDisplayRow {
  id: RealmId;
  displayName: string;
}

/** Static realm picker order — Verdana first, then the other three. */
export const REALM_PICKER_ORDER: readonly RealmId[] = [
  RealmId.VerdanaFields,
  RealmId.SablewindReaches,
  RealmId.FrostmereExpanse,
  RealmId.EmberMire,
];

/** Build the 4-row table consumed by the realm picker renderer. */
export function buildRealmDisplayRows(): RealmDisplayRow[] {
  return REALM_PICKER_ORDER.map((id) => ({
    id,
    displayName: REALM_METADATA[id].displayName,
  }));
}

/**
 * Normalize a raw name input per spec §2:
 *   - Strip characters outside Unicode letters, spaces, apostrophes, hyphens.
 *   - Trim leading/trailing whitespace.
 *   - Clamp to NAME_MAX_LENGTH characters.
 *   - Whitespace-only or empty results return null (the "nameless stranger"
 *     branch — explicitly allowed per spec §2).
 */
export function normalizeCharacterName(raw: string): string | null {
  // Allow Unicode letters via \p{L}, plus the four punctuation/space classes.
  const stripped = raw.replace(/[^\p{L}\s'-]/gu, '');
  const trimmed = stripped.trim();
  if (trimmed.length === 0) return null;
  return trimmed.slice(0, NAME_MAX_LENGTH);
}

/** Persisted shape of the local character-creation draft. */
export interface CharacterDraft {
  /** Schema version — bump when shape changes; loader stays back-compat. */
  version: 1;
  /** Trimmed/normalized name, or null for the nameless-stranger branch. */
  name: string | null;
  /** Vocation key (canonical lowercase string from VocationKey). */
  vocation: VocationKey;
  /** Starter realm — defaults to Verdana per spec §5.2. */
  realm: RealmId;
  /** Wall-clock ms when the draft was written; used for staleness telemetry. */
  createdAtMs: number;
}

/** Build a draft object from picker selections. Pure; no I/O. */
export function buildDraft(input: {
  name: string | null;
  vocation: VocationKey;
  realm: RealmId;
  now: number;
}): CharacterDraft {
  return {
    version: 1,
    name: input.name,
    vocation: input.vocation,
    realm: input.realm,
    createdAtMs: input.now,
  };
}

/**
 * Serialize a draft to a JSON string suitable for localStorage. The server
 * persistence follow-up will read this string back through `parseDraft`.
 */
export function serializeDraft(draft: CharacterDraft): string {
  return JSON.stringify(draft);
}

/**
 * Parse a previously-serialized draft. Returns null on any shape mismatch
 * (unknown vocation, unknown realm, missing field) so callers can treat
 * tampered or stale data as "no draft" rather than crash.
 */
export function parseDraft(raw: string | null | undefined): CharacterDraft | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  if (obj.version !== 1) return null;
  const vocation = obj.vocation;
  if (typeof vocation !== 'string') return null;
  if (!isVocationKey(vocation)) return null;
  const realm = obj.realm;
  if (typeof realm !== 'string') return null;
  if (!isRealmId(realm)) return null;
  const name = obj.name === null ? null : typeof obj.name === 'string' ? obj.name : undefined;
  if (name === undefined) return null;
  const createdAtMs = typeof obj.createdAtMs === 'number' ? obj.createdAtMs : 0;
  return {
    version: 1,
    name,
    vocation,
    realm,
    createdAtMs,
  };
}

/** Type guard — narrow an arbitrary string to a VocationKey value. */
export function isVocationKey(value: string): value is VocationKey {
  for (const key of VOCATION_DISPLAY_ORDER) {
    if (value === key) return true;
  }
  return false;
}
